import {t, useSiteLanguage} from "../../../i18n";
import {useCallback, useEffect, useRef, useState} from 'react';
import type {ResponsiveImageAsset} from '../../../assets/imageAssets';
import ProgressiveImage from '../../../commonComponent/image/ProgressiveImage';
import './diapo.css';

interface DiapoProps {
    images: ResponsiveImageAsset[];
}

const Diapo = ({images}: DiapoProps) => {
    useSiteLanguage();
    const sectionRef = useRef<HTMLElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const playButtonRef = useRef<HTMLButtonElement>(null);
    const activeIndexRef = useRef(0);
    const scrollFrame = useRef<number | undefined>(undefined);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isDocumentVisible, setIsDocumentVisible] = useState(!document.hidden);
    const [reducedMotion, setReducedMotion] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        const mobileQuery = window.matchMedia('(max-width: 639px)');
        const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        const updateMobile = () => setIsMobile(mobileQuery.matches);
        const updateMotion = () => setReducedMotion(motionQuery.matches);
        const updateVisibility = () => setIsDocumentVisible(!document.hidden);
        updateMobile();
        updateMotion();
        mobileQuery.addEventListener('change', updateMobile);
        motionQuery.addEventListener('change', updateMotion);
        document.addEventListener('visibilitychange', updateVisibility);

        const observer = new IntersectionObserver(([entry]) => {
            setIsVisible(entry.isIntersecting);
        }, {threshold: 0.25});
        if (sectionRef.current) observer.observe(sectionRef.current);

        return () => {
            mobileQuery.removeEventListener('change', updateMobile);
            motionQuery.removeEventListener('change', updateMotion);
            document.removeEventListener('visibilitychange', updateVisibility);
            observer.disconnect();
            if (scrollFrame.current) cancelAnimationFrame(scrollFrame.current);
        };
    }, []);

    const scrollToPhoto = useCallback((index: number, automatic = false) => {
        const track = trackRef.current;
        if (!track || !images.length) return;
        const nextIndex = (index + images.length) % images.length;
        // Wrapping the strip should not sweep through every intermediate photo.
        const wraps = Math.abs(nextIndex - activeIndexRef.current) > 1;
        track.scrollTo({left: nextIndex * track.clientWidth,
            behavior: reducedMotion || (automatic && wraps) ? 'auto' : 'smooth'});
    }, [images.length, reducedMotion]);

    useEffect(() => {
        if (!isMobile || !isVisible || !isDocumentVisible || reducedMotion || isPaused || isHovered || images.length < 2) return;
        const interval = setInterval(() => scrollToPhoto(activeIndexRef.current + 1, true), 4000);
        return () => clearInterval(interval);
    }, [images.length, isDocumentVisible, isHovered, isMobile, isPaused, isVisible, reducedMotion, scrollToPhoto]);

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;
        const observer = new ResizeObserver(() => {
            if (isMobile) {
                track.scrollTo({left: activeIndexRef.current * track.clientWidth, behavior: 'auto'});
            }
        });
        observer.observe(track);
        return () => observer.disconnect();
    }, [isMobile]);

    const selectPhoto = (index: number) => {
        setIsPaused(true);
        scrollToPhoto(index);
    };

    return (
        <section id="image-list" className="photo-strip m-3 mt-4" ref={sectionRef}
                 aria-label={t("home.slideshow")}
                 onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}
                 onFocusCapture={event => { if (event.target !== playButtonRef.current) setIsPaused(true); }}>
            <div className="photo-strip__track" ref={trackRef} tabIndex={0}
                 aria-label={t("home.slideshowList")} onPointerDown={() => setIsPaused(true)}
                 onKeyDown={event => {
                     if (isMobile && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
                         event.preventDefault();
                         selectPhoto(activeIndexRef.current + (event.key === 'ArrowLeft' ? -1 : 1));
                     }
                 }}
                 onScroll={() => {
                     if (!isMobile || scrollFrame.current) return;
                     scrollFrame.current = requestAnimationFrame(() => {
                         scrollFrame.current = undefined;
                         const track = trackRef.current;
                         if (!track || !track.clientWidth) return;
                         const index = Math.max(0, Math.min(images.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
                         activeIndexRef.current = index;
                         setActiveIndex(index);
                     });
                 }}>
                {images.map((asset, index) => (
                    <div className="photo-strip__slide" key={asset.name} role="group"
                         aria-label={t("home.slidePosition", {number: index + 1, total: images.length})}>
                        <ProgressiveImage asset={asset} sizes={`(min-width: 1280px) max(25vw, ${384 * asset.width / asset.height}px), (min-width: 768px) max(25vw, ${240 * asset.width / asset.height}px), (min-width: 640px) max(50vw, ${240 * asset.width / asset.height}px), max(100vw, ${400 * asset.width / asset.height}px)`}
                                          alt={t('gallery.photo', {number: index + 1})} draggable={false}/>
                    </div>
                ))}
            </div>

            <div className="photo-strip__controls">
                <button className="photo-strip__arrow photo-strip__arrow--previous" type="button"
                        aria-label={t("gallery.previous")} onClick={() => selectPhoto(activeIndexRef.current - 1)}>&#8249;</button>
                <div className="photo-strip__dots">
                    {images.map((asset, index) => (
                        <button type="button" key={asset.name} aria-label={`Afficher la photo ${index + 1}`}
                                aria-current={index === activeIndex ? 'true' : undefined}
                                onClick={() => selectPhoto(index)}><span/></button>
                    ))}
                </div>
                <span className="photo-strip__count" aria-live={isPaused ? 'polite' : 'off'}>
                    {activeIndex + 1} / {images.length}
                </span>
                {!reducedMotion && <button className="photo-strip__play" type="button" ref={playButtonRef}
                                            aria-label={isPaused ? 'Reprendre le diaporama' : 'Mettre le diaporama en pause'}
                                            onClick={() => setIsPaused(paused => !paused)}>
                    {isPaused ? '▶' : 'Ⅱ'}
                </button>}
                <button className="photo-strip__arrow photo-strip__arrow--next" type="button"
                        aria-label={t("gallery.next")} onClick={() => selectPhoto(activeIndexRef.current + 1)}>&#8250;</button>
            </div>
        </section>
    );
};

export default Diapo;
